import React, { useCallback, useState } from 'react';
import styles from '../../forms/shared/styles.module.css';
import checklistStyles from '../checklistStyles.module.css';
import LogoBanner from '../../forms/shared/LogoBanner';
import QuestionSection from './QuestionSection';
import ChecklistTable from './ChecklistFormsTable';
import ThankYouSection from './ThankYouSection';
import { NavigatorFormState } from './NavigatorChecklist';
import { Alert } from '@tcl-boron-prefabs/alert';
import { Information, Success } from '@tcl-boron-icons/icons';
import NoFormsModal from './NoFormsModal';
import {
  ConsentAppForm,
  FinancialAidConsentAppDto,
  FinancialAssistanceCalculationStatus,
} from '@tempus/patient-forms-service-shared';
import { ValidatedValues } from './Verification';
import classNames from 'classnames';

type ChecklistProps = {
  navigatorState: NavigatorFormState[];
  validatedValues?: ValidatedValues;
  showSuccessBanner: boolean;
  successBannerFAInfo?: FinancialAidConsentAppDto;
  formUrlPrefix: string;
};

const getMainComponent = (
  incompleteForms: ConsentAppForm[],
  completeForms: ConsentAppForm[],
  formUrlPrefix: string,
) => {
  if (!incompleteForms.length && !completeForms.length) {
    return <NoFormsModal />;
  } else {
    return (
      <>
        {incompleteForms.length > 0 && (
          <ChecklistTable forms={incompleteForms} complete={false} formUrlPrefix={formUrlPrefix} />
        )}
        {completeForms.length > 0 && (
          <ChecklistTable
            forms={completeForms}
            complete={true}
            tableTitle={`Completed Documents`}
            formUrlPrefix={formUrlPrefix}
          />
        )}
      </>
    );
  }
};

const getFormsByStatus = (complete: boolean, navigatorState: NavigatorFormState[]) => {
  const formList: ConsentAppForm[] = [];
  for (const formState of navigatorState) {
    if (formState.complete === complete) {
      formList.push(formState.formModel);
    }
  }

  return formList;
};

const Checklist: React.FunctionComponent<ChecklistProps> = (props) => {
  const [incompleteForms] = useState<ConsentAppForm[]>(getFormsByStatus(false, props.navigatorState));
  const [completeForms] = useState<ConsentAppForm[]>(getFormsByStatus(true, props.navigatorState));
  const [showSuccessBanner, setShowSuccessBanner] = useState<boolean>(props.showSuccessBanner);
  const dismissBanner = useCallback(() => setShowSuccessBanner(false), [setShowSuccessBanner]);

  const useSuccessIcon =
    !props.successBannerFAInfo || props.successBannerFAInfo.status === FinancialAssistanceCalculationStatus.APPROVED;

  return (
    <div>
      <LogoBanner />
      <div className={styles.content}>
        <div className={checklistStyles.checklistAlert}>
          {showSuccessBanner && (
            <Alert status={useSuccessIcon ? 'success' : 'warning'} isDismissable onDismiss={dismissBanner}>
              {{
                icon: useSuccessIcon ? <Success /> : <Information />,
                content: props.successBannerFAInfo ? (
                  <div className={styles.alertContent}> {props.successBannerFAInfo.statement} </div>
                ) : (
                  'Form submitted successfully.'
                ),
                title: props.successBannerFAInfo ? (
                  <div className={styles.alertTitle}>{props.successBannerFAInfo.title}</div>
                ) : undefined,
              }}
            </Alert>
          )}
        </div>
        {incompleteForms.length === 0 && completeForms.length > 0 && <ThankYouSection />}
        <div className={classNames([styles.title, styles.checklistHeader])}>Patient Forms</div>
        {getMainComponent(incompleteForms, completeForms, props.formUrlPrefix)}
        <QuestionSection />
      </div>
    </div>
  );
};

export default Checklist;
